import React, { useMemo, useState } from "react"
import { Receipt, ReceiptDialog } from "../../../../../receipt/pages/ReceiptDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { MRT_ColumnDef } from "material-react-table"
import { ChannelTypeChip } from "../../../../../merchant/merchant/production/components/ChannelTypeChip"
import { PaymentMethodCell } from "../../../../../merchant/merchant/production/components/PaymentMethodCell"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { TableNumberOfItemsBox, TableSummaryWrap } from "../../../../../../shared/components/table/TableSummaryBox"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { MenuItem } from "@mui/material"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { fetchPurchasesByPeriod, FetchPurchasesResponse, UpcomingOrdersPurchases } from "../purchasesApi"
import { Order } from "../../../../../merchant/controlKitchenTypes"
import { DatePeriod } from "../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { useExportPurchasesToCsv } from "../hooks/useExportPurchases"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"

import { TimeOrDatePicker } from "../../../../../../shared/components/table/TimeOrDatePicker"

function transferData(data: FetchPurchasesResponse) {
    return data.orders ?? []
}

export function UserPurchases() {
    const [receipt, setReceipt] = useState<Receipt>()
    const [datePeriod, setDatePeriod] = useState<DatePeriod>(DatePeriod.ALL)

    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState({
        startDate: startDate,
        endDate: endDate,
    })

    const { l10n } = useAppLocalization()

    const purchasesList = useApiQuery(fetchPurchasesByPeriod, {
        queryName: UpcomingOrdersPurchases.UPCOMING_ORDERS_PURCHASES,
        dependencies: {
            period: isDatePicker ? undefined : datePeriod,
            startDate: isDatePicker ? dateState.startDate.toISODate() : undefined,
            endDate: isDatePicker ? dateState.endDate.toISODate() : undefined,
        },
        select: transferData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(purchasesList)

    const columns = useMemo<MRT_ColumnDef<Order>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("production-order-number"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("production-order-time"),
                },
                (row) => row.created,
            ),

            columnFactory.typeColumn({
                header: l10n.getString("production-order-payment-channel"),
                accessorKey: "paymentDetails.channel.displayText",
                Cell: ({ row }) => <ChannelTypeChip channel={row.original.paymentDetails.channel} />,
            }),
            columnFactory.typeColumn({
                header: l10n.getString("production-order-payment-status"),
                accessorKey: "paymentDetails.method",
                Cell: ({ row }) => <PaymentMethodCell paymentDetails={row.original.paymentDetails} />,
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("office-purchase-amount"),
                    id: "amount",
                },
                (row) => row.amount,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    function showReceipt(row: Order) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const handleChangeTimeOrDatePicker = () => {
        setIsDatePicker((prev) => !prev)
    }

    const { exportPurchasesToCsv } = useExportPurchasesToCsv()

    return (
        <>
            <ApiQueryTable
                columns={columns}
                enableRowSelection
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="office-purchase-receipt">
                        <MenuItem
                            onClick={() => {
                                showReceipt(row.original)
                                closeMenu()
                            }}
                            key={`receipt-${row.original.id}`}
                        >
                            Receipt
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton
                            onClick={() => {
                                exportPurchasesToCsv(purchasesList.data)
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <TimeOrDatePicker
                            isDatePicker={isDatePicker}
                            dateState={dateState}
                            setDateState={setDateState}
                            timePeriod={datePeriod}
                            handleChangeTimePeriod={setDatePeriod}
                            handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                        />
                    </StandardTableFilter>
                )}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                            <TableNumberOfItemsBox data={table.getFilteredRowModel().rows} title="Number of orders" />
                        </LocalizedStrict>
                    </TableSummaryWrap>
                )}
            />

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={purchasesList.data}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
